// Sana Console — query volume, intents, zero-result, latency, geography.
import React, { useState, useEffect } from 'react';
import { ConsoleShell, KPI, Panel, SectionLabel, Bar, StatLine, Empty, Drawer, Donut, Heatmap, PALETTE, useConsoleTheme, useIsMobile } from '../components/Console';
import { useSanaDeepStats, useSearchStats, useSanaStats } from '../hooks/useAnalytics';
import { supabase } from '../supabaseClient';
import { setFocus, takeFocus } from '../utils/consoleBus';

const fmt = n => (n == null ? '—' : Number(n).toLocaleString());
function usePerf() {
  const [d, setD] = useState(null);
  useEffect(() => { let c = false; supabase.rpc('admin_perf_summary').then(({ data }) => { if (!c) setD(data); }); return () => { c = true; }; }, []);
  return d;
}
function useHeatmap() {
  const [d, setD] = useState(null);
  useEffect(() => { let c = false; supabase.rpc('admin_activity_heatmap').then(({ data }) => { if (!c) setD(data); }); return () => { c = true; }; }, []);
  return d;
}

function SanaSegInspector({ seg }) {
  const { t } = useConsoleTheme();
  const [d, setD] = useState(null);
  useEffect(() => {
    let c = false; setD(null);
    supabase.rpc('admin_sana_breakdown', { p_dim: seg.dim, p_val: seg.val }).then(({ data }) => { if (!c) setD(data || {}); });
    return () => { c = true; };
  }, [seg]);
  if (!d) return <Empty>Loading {seg.val}…</Empty>;
  const queries = d.top_queries || []; const maxQ = Math.max(1, ...queries.map(x => x.n));
  const extra = d.extra || []; const maxE = Math.max(1, ...extra.map(x => x.n));
  return (
    <div>
      <div style={{ fontSize:'1.4rem', fontWeight:500, color:t.tx }}>{fmt(d.n)}</div>
      <div style={{ fontSize:'0.78rem', color:t.mut, marginBottom:12 }}>{seg.dim} · {seg.val}</div>
      <StatLine label="Result rate" value={`${d.result_rate || 0}%`} color={t.up} />
      <StatLine label="Avg latency" value={d.avg_latency != null ? `${fmt(d.avg_latency)}ms` : '—'} />
      <div style={{ height:1, background:t.bd, margin:'12px 0' }} />
      <div style={{ fontSize:'0.74rem', color:t.mut, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>Top queries</div>
      {queries.length === 0 ? <Empty>No queries.</Empty> : queries.map(x => <Bar key={x.query} label={x.query} value={fmt(x.n)} pct={Math.round(x.n / maxQ * 100)} labelWidth={140} />)}
      <div style={{ fontSize:'0.74rem', color:t.mut, textTransform:'uppercase', letterSpacing:'.06em', margin:'12px 0 8px' }}>{d.extra_label || 'Breakdown'}</div>
      {extra.length === 0 ? <Empty>No data.</Empty> : extra.map(x => <Bar key={x.k} label={x.k} value={fmt(x.n)} pct={Math.round(x.n / maxE * 100)} color={t.accent} labelWidth={140} />)}
    </div>
  );
}

function Body({ onNav }) {
  const { t } = useConsoleTheme();
  const mobile = useIsMobile();
  const [seg, setSeg] = useState(null);
  useEffect(() => { const f = takeFocus(['sana-country', 'sana-intent']); if (f) setSeg({ dim: f.type === 'sana-country' ? 'country' : 'intent', val: f.value }); }, []);
  const { data: deep } = useSanaDeepStats();
  const { data: search } = useSearchStats();
  const { data: sana } = useSanaStats();
  const perf = usePerf();
  const heat = useHeatmap();
  const lat = perf && perf.sana_latency_ms_24h;
  const intents = deep.topIntents || [];
  const maxI = Math.max(1, ...intents.map(x => x.count));
  const countries = (deep.topCountries && deep.topCountries.length ? deep.topCountries : search.topCountries) || [];
  const maxC = Math.max(1, ...countries.map(x => x.count));
  const intentDonut = (() => {
    const ii = intents.slice(0, 6);
    const sum = ii.reduce((a, x) => a + x.count, 0);
    const arr = ii.map((x, i) => ({ label: x.intent, value: x.count, color: PALETTE[i % PALETTE.length] }));
    const other = Math.max(0, (sana.total || 0) - sum);
    if (other > 0) arr.push({ label: 'Other', value: other, color: t.mut });
    return arr;
  })();
  const queries = search.topQueries || [];
  const maxQ = Math.max(1, ...queries.map(x => x.count));
  const col = n => (mobile ? '1fr 1fr' : `repeat(${n},1fr)`);
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns: col(6), gap:10 }}>
        <KPI label="Sana queries" value={fmt(sana.total)} sub="all-time" accent="#5a8fa6" />
        <KPI label="Today" value={fmt(sana.todayCount)} accent={t.accent} />
        <KPI label="Result rate" value={`${deep.resultRate || sana.resultRate || 0}%`} subColor={t.up} accent="#17a86a" />
        <KPI label="Avg latency" value={deep.avgLatencyMs != null ? `${fmt(deep.avgLatencyMs)}ms` : (lat && lat.p50 != null ? `${fmt(lat.p50)}ms` : '—')} sub="p50 · 24h" accent="#9F7E4F" />
        <KPI label="Zero-result" value={`${search.zeroResultRate || 0}%`} sub="searches" subColor={(search.zeroResultRate || 0) > 30 ? t.warn : t.mut} accent="#d97706" />
        <KPI label="Searches" value={fmt(search.total)} sub={`${fmt(search.todayCount)} today`} accent="#314F5C" />
      </div>

      <SectionLabel>Intent & queries</SectionLabel>
      <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap:12 }}>
        <Panel title="◇ Top intents">
          {intents.length === 0 ? <Empty>No Sana intent data yet.</Empty>
            : intents.map(x => <Bar key={x.intent} label={x.intent} value={fmt(x.count)} pct={Math.round(x.count / maxI * 100)} labelWidth={140} onClick={() => setSeg({ dim:'intent', val:x.intent })} />)}
        </Panel>
        <Panel title="◔ Top search queries">
          {queries.length === 0 ? <Empty>No searches recorded yet.</Empty>
            : queries.slice(0, 8).map(x => <Bar key={x.query} label={x.query} value={fmt(x.count)} pct={Math.round(x.count / maxQ * 100)} labelWidth={150} />)}
        </Panel>
      </div>

      <SectionLabel>Geography & performance</SectionLabel>
      <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap:12 }}>
        <Panel title="◷ Queries by country">
          {countries.length === 0 ? <Empty>No country data yet.</Empty>
            : countries.slice(0, 6).map(x => <Bar key={x.country} label={x.country} value={fmt(x.count)} pct={Math.round(x.count / maxC * 100)} labelWidth={120} onClick={() => setSeg({ dim:'country', val:x.country })} />)}
        </Panel>
        <Panel title="◈ Latency & signals" right="24h">
          <StatLine label="p50 latency" value={lat && lat.p50 != null ? `${fmt(lat.p50)}ms` : '—'} />
          <StatLine label="p95 latency" value={lat && lat.p95 != null ? `${fmt(lat.p95)}ms` : '—'} color={t.warn} />
          <StatLine label="p99 latency" value={lat && lat.p99 != null ? `${fmt(lat.p99)}ms` : '—'} />
          <div style={{ height:1, background:t.bd, margin:'10px 0' }} />
          <StatLine label="Profile-used rate" value={`${deep.profileRate || 0}%`} />
          <StatLine label="Near-me rate" value={`${deep.nearMeRate || 0}%`} />
          <StatLine label="Emergency rate" value={`${deep.emergencyRate || 0}%`} color={(deep.emergencyRate || 0) > 0 ? t.danger : t.tx} />
          <StatLine label="Tokens (total)" value={fmt(deep.totalTokens)} />
        </Panel>
      </div>

      <SectionLabel>Intent mix</SectionLabel>
      <Panel>
        {intents.length === 0 ? <Empty>No intent data yet.</Empty> : <Donut data={intentDonut} center={{ value: fmt(sana.total), label: 'queries' }} />}
      </Panel>

      <SectionLabel>Activity rhythm</SectionLabel>
      <Panel title="▵ When traffic happens" right={heat ? `weekday × hour · last ${heat.window_days || 90}d` : ''}>
        {!heat ? <Empty>Loading activity grid…</Empty> : <Heatmap cells={heat.cells} max={heat.max} caption="Sana + search events by weekday × hour (UTC)" />}
      </Panel>

      <Drawer open={!!seg} onClose={() => setSeg(null)} title={seg ? seg.val : 'Segment'}>
        {seg && <SanaSegInspector seg={seg} />}
        {seg && seg.dim === 'country' && <div style={{ marginTop:16 }}><button onClick={() => { setFocus({ type:'country', value:seg.val }); onNav && onNav('directory'); }} style={{ fontSize:'0.82rem', padding:'7px 12px', borderRadius:7, cursor:'pointer', border:`1px solid ${t.bd}`, background:t.period, color:t.tx, fontWeight:500 }}>Open in Directory →</button></div>}
      </Drawer>
    </div>
  );
}

export default function SanaConsole(props) {
  return <ConsoleShell title="Sana Console"><Body onNav={props.onNav} /></ConsoleShell>;
}
